import { bodyComposition, ScanProgress } from 'fitbeat.models';
import React, { FunctionComponent, useState } from 'react';
import { translate } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { Button, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import {AthleteModeSelection} from './athlete-mode-selection';
import { BodyscaleBodyTypeSelector } from './bodyscale-bodytype-selector';

interface IProps {
  from: ScanProgress;
  bodyCompositionType: bodyComposition;
  athleteMode: boolean;
  isSaving: boolean;
  t: (key: string) => string;
  back(): void;
  redoBodyAssessment(): void;
  updateBodyCompositionAndConditioning(composition: any, athleteModeSelected: boolean): any;
}

const BodyscaleBodyTypes: FunctionComponent<IProps> = ({
  t,
  back,
  redoBodyAssessment,
  updateBodyCompositionAndConditioning,
  bodyCompositionType,
  isSaving,
  athleteMode,
}) => {
  const [composition, setComposition] = useState<bodyComposition | null>(
    bodyCompositionType,
  );
  const [athleteModeSelected, setAthleteModeSelected] = useState<boolean>(
    athleteMode,
  );
  const changeBodyComposition = (setting: bodyComposition) => {
    setComposition(setting);
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View style={{marginBottom: defaultScale(60), alignItems: 'center', marginTop: defaultScale(10)}}>
          <Text style={styles.selectionHeading}>
              {`${t('BodyType')}: ${t(`${composition}`)}`}
         </Text>
          <Text style={styles.description}>{composition && t(`${composition}_Explainer`)}</Text>
          <BodyscaleBodyTypeSelector
            composition={composition}
            onPress={async (setting) => changeBodyComposition(setting)}
          />
        </View>
        <View style={styles.horizontalDivider}/>
        <View style={{alignItems: 'center'}}>
        <Text style={styles.selectionHeading}>
            {`${t('BodyConditioning')}: ${athleteModeSelected ? t('athlete') : t('standard')}`}
        </Text>
        <Text style={styles.description}>
            {athleteModeSelected ? t('isConditionedAthlete') : t('isNotConditionedAthlete')}
        </Text>
        <AthleteModeSelection
            athleteModeSelected={athleteModeSelected}
            onPress={() => setAthleteModeSelected(!athleteModeSelected)}
        />
        </View>
      </ScrollView>
      <View style={styles.footer}>
        <Button
          title={t('stillUnsureBodyType')}
          onPress={redoBodyAssessment}
          fontColor={Theme.PRIMARY}
          buttonType={ButtonTypes.TRANSPARENT}
          containerStyle={styles.linkButtonContainer}
        />
        <View style={styles.buttonsContainer}>
          <Button
            title={t('back')}
            onPress={back}
            fontColor={Theme.PRIMARY}
            buttonType={ButtonTypes.OUTLINE}
            containerStyle={styles.backButton}
          />
          <Button
            title={t('updateBodyType')}
            onPress={() => updateBodyCompositionAndConditioning(composition, athleteModeSelected)}
            isLoading={isSaving}
            fontColor={Theme.WHITE}
            buttonType={ButtonTypes.NORMAL}
            containerStyle={styles.submitButton}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  buttonsContainer: {
    flexDirection: 'row',
  },
  footer: {
    justifyContent: 'flex-end',
  },
  linkButtonContainer: {
    marginTop: defaultScale(20)
  },
  backButton: {
    flex: 1,
    flexBasis: 0,
    marginRight: 8,
  },
  submitButton: {
    flex: 2,
    flexBasis: 0,
  },
  title: {
    color: Theme.BLACK,
    fontSize: 20,
    fontFamily: Theme.Custom600,
    marginBottom: 10,
  },
  description: {
      ...Theme.textStyle.small,
      textAlign: 'center',
  },
  selectionHeading: {
      ...Theme.textStyle.heading2,
      marginBottom: defaultScale(10),
  },
  horizontalDivider: {
    width: '100%',
    height: defaultScale(0.5),
    backgroundColor: Theme.GREY_FIGMA_LIGHT,
    bottom: defaultScale(30),
  },
});

export default translate(['bodyscan'])(BodyscaleBodyTypes);
